import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryContent } from '@/mocks/categories';

type CategorySlug = 'movies' | 'tv' | 'variety' | 'anime';

const categoryTabs: { slug: CategorySlug; label: string; icon: string }[] = [
  { slug: 'movies', label: '电影', icon: 'ri-movie-2-line' },
  { slug: 'tv', label: '电视剧', icon: 'ri-tv-2-line' },
  { slug: 'variety', label: '综艺', icon: 'ri-emotion-laugh-line' },
  { slug: 'anime', label: '动漫', icon: 'ri-rocket-line' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const activeSlug: CategorySlug = (slug && ['movies', 'tv', 'variety', 'anime'].includes(slug))
    ? (slug as CategorySlug)
    : 'movies';

  const category = categoryContent[activeSlug];

  const handleTabChange = (newSlug: CategorySlug) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: category.name, path: `/category/${activeSlug}` },
              ]}
            />
          </div>
        </div>

        <div className="bg-background-100 pb-4 border-b border-background-200">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.slug}
                  onClick={() => handleTabChange(tab.slug)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === tab.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500">
              {category.description}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {category.items.map((item) => (
              <Link
                key={item.id}
                to={`/article/${item.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-2 left-2">
                    <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      {item.rating}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white/80 text-xs">{item.category}</span>
                      <span className="text-white/50 text-xs">·</span>
                      <span className="text-white/50 text-xs">{item.year}</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {item.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                  {item.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${category.name} - 蜜桃影视`,
              description: category.description,
              url: `https://entervue.github.io/category/${activeSlug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '蜜桃影视',
                url: 'https://entervue.github.io',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://entervue.github.io/' },
                  { '@type': 'ListItem', position: 2, name: category.name, item: `https://entervue.github.io/category/${activeSlug}` },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}